import { computed } from 'vue'
import { isFunction } from '@/global/utils/utils'
import { useActiveStatus } from '@/modules/road-maintenance-module/winter-service-v2/common/composables/useActiveStatus'
import { useResponsiblePerson } from '@/modules/road-maintenance-module/winter-service-v2/common/composables/useResponsiblePerson'
import { BASES_TRANSLATION_NAMESPACE as NS } from '@/modules/road-maintenance-module/winter-service-v2/bases/config/constants'

const COMMENT_ROWS = 6

/**
 * The one field list behind both the base details and the base create screen, so
 * the two can never describe a base differently.
 */
export function useBaseFields (options) {
  const { translate, openGeolocationDialogFn } = options || {}

  const { statusItems } = useActiveStatus({ trans: translate })
  const {
    responsiblePersonField,
    responsiblePersonEmailField,
    responsiblePersonPhoneField
  } = useResponsiblePerson()

  // The address is picked on a map, the same way every other module does it - the
  // dialog writes the address back together with the coordinates the map panel
  // then renders its marker from.
  const onOpenGeolocationDialog = (event) => {
    if (!isFunction(openGeolocationDialogFn)) {
      return
    }

    openGeolocationDialogFn(event)
  }

  const fields = computed(() => ([
    {
      key: 'name',
      label: translate(`${NS}.base_name`),
      creatable: true,
      editable: true,
      visible: true
    },
    {
      key: 'associated_base_name',
      label: translate(`${NS}.associated_base_name`),
      creatable: true,
      editable: true,
      visible: true
    },
    {
      key: 'address',
      label: translate(`${NS}.address`),
      appendIcon: 'mdi-map-marker',
      appendIconClick: onOpenGeolocationDialog,
      creatable: true,
      editable: true,
      visible: true
    },
    {
      key: 'status',
      label: translate(`${NS}.status`),
      type: 'autocomplete',
      items: statusItems.value,
      // A brand new base has no status to pick yet - the backend starts it off as
      // active - so the field only shows up once the record exists. `creatable`
      // stays true regardless: Content.vue's applyFormValuesForSend uses it to
      // decide what gets sent on EVERY save, edit included.
      visible: (fieldsRenderer) => !fieldsRenderer?.createMode,
      creatable: true,
      editable: true
    },
    {
      key: 'comment',
      label: translate(`${NS}.comment`),
      type: 'textarea',
      rows: COMMENT_ROWS,
      creatable: true,
      editable: true,
      visible: true
    },
    // Typed, all three of them - the contact is a snapshot rather than a link to
    // someone in the people register. See useResponsiblePerson.
    responsiblePersonField(translate(`${NS}.responsible_person`)),
    responsiblePersonEmailField(translate(`${NS}.responsible_person_email`)),
    responsiblePersonPhoneField(translate(`${NS}.responsible_person_phone`))
  ]))

  return {
    fields
  }
}
